import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Modal,
  Typography,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import type {
  RequestedPicturesPlotDataResponse,
  PropertySelectionRow,
} from "../../types";
import { ChibanKaokuNumberSearchModal } from "@/components/KaokuNumberSearch/ChibanKaokuNumberSearchModal";
import { AcquireBookModal } from "../../../acquireBookStatus/components/AcquireBookModal";
import type { ISingleBookAcquireDialogData } from "@/types/singleBookAcquireDialog";
import { createAcquireMultipleBookHandoffData } from "@/utils/utilsAcquireBook";
import { DetailRow } from "./DetailRow";
import { MortgageesSection } from "./MortgageesSection";
import { TagsAndRemarksSection } from "./TagsAndRemarksSection";
import { formatUpdateTime } from "./utils";
import { NYHalfWidthNumberToFillWidthNumber } from "@/utils/utils";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { useGeoSpaceApi } from "@/features/map/hooks/useGeospaceSearch";
import type { GeoSpaceChiban } from "@/features/map/types";
import type { AdditionalFieldConfig } from "@/features/accountSettings/types/additionalFields";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { AllItemsDialog } from "./AllItemsDialog";
import { useCreateZipWithFiles } from "../../api/postCreateZipWithFiles";
import { toast } from "react-toastify";
import { useRequestedPicturesHistory } from "../../hooks/useRequestedPicturesHistory";

interface RealEstateDetailDialogProps {
  plot: RequestedPicturesPlotDataResponse | null;
  onClose: () => void;
  handlePinSave?: () => void;
  getInitialTags?: () => string[];
  onTagsChange?: (tags: string[]) => void;
  getInitialRemarks?: () => string;
  onRemarksChange?: (remarks: string) => void;
  getInitialAdditionalFields?: () => string;
  onAdditionalFieldsChange?: (additionalFields: string) => void;
  availableFields?: AdditionalFieldConfig;
  hasAvailableFields?: boolean;
  crm?: boolean;
}

/**
 * 登記情報を比較して最新の登記種別を取得する
 * @param data 取得済み登記詳細データ
 * @returns "OWNER" | "ALL_MATTERS" | null
 */
export const compareLatestRequest = (
  data: RequestedPicturesPlotDataResponse
): "OWNER" | "ALL_MATTERS" | null => {
  const ownerLatestRequestDate = data.owner?.pictureUpdateTime;
  const allMattersLatestRequestDate = data.allMatter?.pictureUpdateTime;

  if (ownerLatestRequestDate && !allMattersLatestRequestDate) {
    return "OWNER";
  }

  if (!ownerLatestRequestDate && allMattersLatestRequestDate) {
    return "ALL_MATTERS";
  }

  if (ownerLatestRequestDate && allMattersLatestRequestDate) {
    if (ownerLatestRequestDate > allMattersLatestRequestDate) {
      return "OWNER";
    }
    return "ALL_MATTERS"; // 同じ場合は全部事項を優先
  }

  // NOTE: nullとなる場合は想定しない（BE側の実装エラーの可能性がある）
  return null;
};

/**
 * 取得済み登記詳細ダイアログ
 */
export const RealEstateDetailDialog: React.FC<RealEstateDetailDialogProps> = ({
  plot,
  onClose,
  handlePinSave,
  getInitialTags,
  onTagsChange,
  getInitialRemarks,
  onRemarksChange,
  getInitialAdditionalFields,
  onAdditionalFieldsChange,
  availableFields,
  hasAvailableFields,
  crm,
}) => {
  const navigate = useNavigate();
  const isReadOnly = useReadOnlyUser();
  const { geospaceReverseByLatLng, geospaceLegalAffairsReverseByLatlng } =
    useGeoSpaceApi();
  const createZipAPI = useCreateZipWithFiles();
  const {
    trigger: fetchHistory,
    data: historyData,
    isMutating: isLoadingHistory,
  } = useRequestedPicturesHistory();

  // 住居表示の表示文言
  const [addressDisplay, setAddressDisplay] = useState<string>("");
  // 最新登記取得日を全て表示するダイアログの表示非表示を制御するState
  const [showAllLatestRequestDatesDialog, setShowAllLatestRequestDatesDialog] =
    useState<boolean>(false);
  // 「家屋番号検索」モーダル表示非表示管理State
  const [kaokuNumberSearchModalIsOpen, setKaokuNumberSearchModalIsOpen] =
    useState<boolean>(false);
  // (単一)登記取得モーダル表示非表示管理State
  const [acquireBookOpen, setAcquireBookOpen] = useState<boolean>(false);
  // PDFダウンロード中の状態管理
  const [isDownloadingOwner, setIsDownloadingOwner] = useState<boolean>(false);
  const [isDownloadingAllMatters, setIsDownloadingAllMatters] =
    useState<boolean>(false);
  // (単一)登記取得モーダルに渡すデータ
  const [singleBookAcquireDialogData, setSingleBookAcquireDialogData] =
    useState<ISingleBookAcquireDialogData>({
      prefecturesId: 0,
      prefectures: "",
      address: "",
      chiban: "",
      kaokuNumber: "",
      realEstateTypeEng: "LAND",
    });

  useEffect(() => {
    (async () => {
      if (!plot) {
        return;
      }
      setAddressDisplay("");
      // 履歴データを取得
      void fetchHistory(plot.landId);

      try {
        const getGeospaceReverseByLatLng = async (
          plot: RequestedPicturesPlotDataResponse
        ): Promise<GeoSpaceChiban | null> => {
          // まずgeospaceのAPIを呼び出し、結果がなければ法務局地番レイヤーのAPIを呼び出す
          const resultReverseByLatLng = await geospaceReverseByLatLng(
            plot.latitude,
            plot.longitude
          );
          if (resultReverseByLatLng) {
            return resultReverseByLatLng;
          }

          const resultLegalAffairsReverseByLatLng =
            await geospaceLegalAffairsReverseByLatlng(
              plot.latitude,
              plot.longitude
            );
          if (resultLegalAffairsReverseByLatLng) {
            return resultLegalAffairsReverseByLatLng;
          }

          return null;
        };

        const reverseByLatLng = await getGeospaceReverseByLatLng(plot);
        if (reverseByLatLng && !!reverseByLatLng.polygon) {
          setAddressDisplay(
            NYHalfWidthNumberToFillWidthNumber(reverseByLatLng.address)
          );
        } else {
          setAddressDisplay("-");
        }
      } catch (error) {
        setAddressDisplay("-");
      }
    })();
  }, [plot, fetchHistory]);

  // 所有者事項ファイルのダウンロードボタンの活性非活性制御
  const isClickableDownloadOwnerButton = (): boolean => {
    if (!plot) {
      return false;
    }
    const ownerLandFilePath = plot.owner?.filePath;
    const ownerBuildingFilePaths = plot.buildings
      ?.map((building) => building.owner?.filePath)
      .filter((filePath) => !!filePath);
    return (
      (!!ownerLandFilePath || !!ownerBuildingFilePaths?.length) &&
      !isDownloadingOwner
    );
  };

  // 全部事項ファイルのダウンロードボタンの活性非活性制御
  const isClickableDownloadAllMattersButton = (): boolean => {
    if (!plot) {
      return false;
    }
    const allMattersFilePath = plot.allMatter?.filePath;
    const allMattersBuildingFilePaths = plot.buildings
      ?.map((building) => building.allMatter?.filePath)
      .filter((filePath) => !!filePath);
    return (
      (!!allMattersFilePath || !!allMattersBuildingFilePaths?.length) &&
      !isDownloadingAllMatters
    );
  };

  if (!plot) {
    return null;
  }

  const downloadPdfFiles = async (filePaths: string[]): Promise<void> => {
    try {
      const response = await createZipAPI.trigger({ filePaths });
      if (!response) {
        throw new Error("ZIP作成APIからレスポンスが返されませんでした");
      }

      const { presignedUrl } = response;
      const link = document.createElement("a");
      link.href = presignedUrl;
      link.download = "documents.zip";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error(
        "PDFダウンロードに失敗しました。時間をおいて再度お試しください。"
      );
    }
  };

  const downloadOwnerPdf = async (): Promise<void> => {
    setIsDownloadingOwner(true);
    try {
      const filePaths: string[] = [];

      // 土地の所有者事項
      const filePath = plot.owner?.filePath;
      if (filePath) {
        filePaths.push(filePath);
      }

      // 建物の所有者事項
      plot.buildings
        .map((building) => building.owner?.filePath)
        .forEach((buildingFilePath) => {
          if (buildingFilePath) {
            filePaths.push(buildingFilePath);
          }
        });

      await downloadPdfFiles(filePaths);
    } finally {
      setIsDownloadingOwner(false);
    }
  };

  const downloadAllMattersPdf = async (): Promise<void> => {
    setIsDownloadingAllMatters(true);
    try {
      const filePaths: string[] = [];

      // 土地の全部事項
      const filePath = plot.allMatter?.filePath;
      if (filePath) {
        filePaths.push(filePath);
      }

      // 建物の全部事項
      plot.buildings
        .map((building) => building.allMatter?.filePath)
        .forEach((buildingFilePath) => {
          if (buildingFilePath) {
            filePaths.push(buildingFilePath);
          }
        });

      await downloadPdfFiles(filePaths);
    } finally {
      setIsDownloadingAllMatters(false);
    }
  };

  const getLatestRequestDateAndTypeDisplay = (
    plot: RequestedPicturesPlotDataResponse
  ): string => {
    const latestRequest = compareLatestRequest(plot);
    if (latestRequest === "OWNER") {
      const ownerLatestRequestDate = plot.owner?.pictureUpdateTime as string;
      return `${formatUpdateTime(ownerLatestRequestDate)}（所有者事項）`;
    }
    if (latestRequest === "ALL_MATTERS") {
      const allMattersLatestRequestDate = plot.allMatter
        ?.pictureUpdateTime as string;
      return `${formatUpdateTime(allMattersLatestRequestDate)}（全部事項）`;
    }
    return "-";
  };

  const getKaokuNumberDisplay = (
    plot: RequestedPicturesPlotDataResponse
  ): string => {
    const kaokuNumbers = plot.buildings?.map(
      (building) => building.kaokuNumber
    );
    if (kaokuNumbers.length === 0) {
      return "-";
    }
    return NYHalfWidthNumberToFillWidthNumber(
      `${plot.prefectureName}${plot.location}${kaokuNumbers.join("、")}`
    );
  };

  const getLandCategoryDisplay = (
    plot: RequestedPicturesPlotDataResponse
  ): string => {
    return plot.allMatter?.landCategory ?? "-";
  };

  const getBuildingTotalFloorAreaExclusiveDisplay = (
    plot: RequestedPicturesPlotDataResponse
  ): string => {
    const area = plot.allMatter?.buildingTotalFloorAreaExclusive;
    if (area !== null && area !== undefined) {
      return `${getFloatValue3Decimal(area)}(㎡)`;
    }
    return "-";
  };

  const getFloatValue3Decimal = (value: number | null): string => {
    if (value === null) {
      return "-";
    }
    return formatNumberWithComma(value.toFixed(3));
  };

  const appendPropertySelectionRows = (rows: PropertySelectionRow[]): void => {
    const states = createAcquireMultipleBookHandoffData(
      "LAND",
      [plot.chiban],
      String(plot.prefectureId),
      plot.prefectureName,
      plot.location,
      rows
    );

    navigate("/acquiremultiplebooks", { state: states });
  };

  return (
    <>
      <Dialog open onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700, pr: 6, position: "relative" }}>
          取得済み登記詳細
          <IconButton
            onClick={onClose}
            aria-label="閉じる"
            sx={{ position: "absolute", top: 8, right: 8 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="text"
              startIcon={
                isDownloadingAllMatters ? (
                  <CircularProgress size={20} />
                ) : (
                  <DownloadOutlinedIcon />
                )
              }
              onClick={() => {
                void downloadAllMattersPdf();
              }}
              disabled={!isClickableDownloadAllMattersButton()}
            >
              全部事項
            </Button>
            <Button
              variant="text"
              startIcon={
                isDownloadingOwner ? (
                  <CircularProgress size={20} />
                ) : (
                  <DownloadOutlinedIcon />
                )
              }
              onClick={() => {
                void downloadOwnerPdf();
              }}
              disabled={!isClickableDownloadOwnerButton()}
            >
              所有者事項
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setShowAllLatestRequestDatesDialog(true);
              }}
            >
              <AccessTimeIcon />
            </Button>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="body2">
              最新登記取得日: {getLatestRequestDateAndTypeDisplay(plot)}
            </Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Box component="section" aria-label="詳細">
            <Grid container spacing={2}>
              <DetailRow label="住居表示" value={addressDisplay} />
              <DetailRow
                label="地番表示"
                value={NYHalfWidthNumberToFillWidthNumber(
                  plot.prefectureName + plot.location + plot.chiban
                )}
              />
              <DetailRow
                label="取得済み家屋番号"
                value={getKaokuNumberDisplay(plot)}
              />
              {plot.allMatter ? (
                <>
                  <Grid item xs={12}>
                    <Grid container spacing={1} alignItems="flex-start">
                      <Tooltip
                        title="全部事項の場合は、推定の最終所有者を表示しています"
                        placement="top"
                      >
                        <Grid item xs={12} sm={4} md={3}>
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", fontWeight: 700 }}
                          >
                            地目（土地）
                          </Typography>
                        </Grid>
                      </Tooltip>
                      <Grid item xs={12} sm={8} md={9}>
                        <Typography variant="body1">
                          {getLandCategoryDisplay(plot)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={1} alignItems="flex-start">
                      <Tooltip
                        title="全部事項の場合は、推定の最終所有者を表示しています"
                        placement="top"
                      >
                        <Grid item xs={12} sm={4} md={3}>
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", fontWeight: 700 }}
                          >
                            地積・床面積（土地）
                          </Typography>
                        </Grid>
                      </Tooltip>
                      <Grid item xs={12} sm={8} md={9}>
                        <Typography variant="body1">
                          {getBuildingTotalFloorAreaExclusiveDisplay(plot)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <DetailRow
                  label="推計地積（土地）"
                  value={`${getFloatValue3Decimal(plot.area)}(㎡)`}
                />
              )}
            </Grid>
          </Box>
          {plot.allMatter && (
            <MortgageesSection
              plot={plot}
              historyData={historyData}
              isLoadingHistory={isLoadingHistory}
            />
          )}
          {crm && (
            <TagsAndRemarksSection
              handlePinSave={handlePinSave}
              getInitialTags={getInitialTags}
              onTagsChange={onTagsChange}
              getInitialRemarks={getInitialRemarks}
              onRemarksChange={onRemarksChange}
              getInitialAdditionalFields={getInitialAdditionalFields}
              onAdditionalFieldsChange={onAdditionalFieldsChange}
              availableFields={availableFields}
              hasAvailableFields={hasAvailableFields}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            width="100%"
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setKaokuNumberSearchModalIsOpen(true);
              }}
            >
              地番・家屋番号検索
            </Button>
            <ReadOnlyTooltip isReadOnly={isReadOnly} fullWidth>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  setSingleBookAcquireDialogData({
                    prefecturesId: plot.prefectureId,
                    prefectures: plot.prefectureName,
                    address: plot.location,
                    chiban: plot.chiban,
                    kaokuNumber: "",
                    realEstateTypeEng: "LAND",
                  });
                  setAcquireBookOpen(true);
                }}
                disabled={isReadOnly}
              >
                登記取得する
              </Button>
            </ReadOnlyTooltip>
          </Stack>
        </DialogActions>
      </Dialog>

      <AllItemsDialog
        open={showAllLatestRequestDatesDialog}
        onClose={() => {
          setShowAllLatestRequestDatesDialog(false);
        }}
        title="登記取得日一覧"
        items={
          historyData
            ? historyData.histories.map((history) => ({
                pictureUpdateTime: formatUpdateTime(history.pictureUpdateTime),
                realEstateType: history.realEstateType,
                pictureType: history.pictureType,
                location: `${history.location}${history.chiban}`,
              }))
            : []
        }
        fieldLabels={{
          pictureUpdateTime: "登記取得日",
          realEstateType: "不動産種別",
          pictureType: "所有者事項または全部事項",
          location: "所在地番または家屋番号",
        }}
        isLoading={isLoadingHistory}
      />

      {/* 家屋番号検索モーダル */}
      <Modal
        open={kaokuNumberSearchModalIsOpen}
        aria-labelledby={"kaoku-number-search-modal"}
        aria-describedby={"kaoku-number-search-modal"}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            outline: "none",
          }}
        >
          <Box
            sx={{
              width: 1600,
              maxWidth: "90%",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 2,
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <ChibanKaokuNumberSearchModal
              variant={"book"}
              chibanList={[
                {
                  prefectureName: plot.prefectureName,
                  location: plot.location,
                  chiban: plot.chiban,
                  bookType: "LAND",
                },
              ]}
              setKaokuNumberSearchModalIsOpen={setKaokuNumberSearchModalIsOpen}
              appendPropertySelectionRows={appendPropertySelectionRows}
            />
          </Box>
        </Box>
      </Modal>

      {/* (単一)登記取得モーダル */}
      <AcquireBookModal
        acquireBookOpen={acquireBookOpen}
        handleClose={() => {
          setAcquireBookOpen(false);
        }}
        row={singleBookAcquireDialogData}
      />
    </>
  );
};
